/**
 * Custom hook for Contagem data management (Itens da Contagem tab)
 *
 * Extracts data-related state from ContagemCompletaPage to reduce component complexity.
 * Manages:
 * - Dados da contagem (items list)
 * - Modified items tracking
 * - Loading/error states
 * - Import/export functionality
 *
 * Usage:
 * const { dadosContagem, updateItem, hasItemChanges, ... } = useContagemData(jobId);
 */
import React, { useState, useCallback, useMemo } from 'react';

export interface DadoContagem {
  id?: number;  // Item ID for deletion/update operations
  cliente?: string;
  regime?: string;
  grupo: string;
  produto: string;
  descricao: string;
  lote: string;
  validade: string;
  faixaAging?: string;
  sistema: number;
  contado: number;
  /** N171: item nunca bipado nem editado (backend mandou null). A tela mostra
   *  `—` em vez de `0`, para nao confundir FALTA com conferencia zerada. */
  nuncaContado?: boolean;
  sobras: number;
  faltas: number;
  segregar?: string;
  // Epic 42: Cirurgias matching fields
  cnpj_cliente?: string;  // CNPJ for matching with cirurgias
  coletado: number;       // quantidade_coletada (preenchido após finalizar conferência)
  faturar: number;        // Sum of matching cirurgias quantities
  pacientes: string;      // Comma-separated list of patient names
  // Epic 44: Quantidade a coletar from Excel (column H)
  coletar: number;
  // Epic 49: Níveis de vencimento do cliente + dias para vencer
  diasParaVencer?: number | null;
  sinalizar_vencimento_1?: number;
  sinalizar_vencimento_2?: number;
  sinalizar_vencimento_3?: number;
  // Epic Reorganização Abas: Lacre e O.C.
  lacre?: string;        // Número do lacre (vem do Excel de retorno Col J)
  ordem_coleta?: string; // O.C. - Ordem de Coleta (INPUT MANUAL)
  // EPIC-SEG: Quantidade segregada do mobile (primary source for "Coletado")
  quantidade_segregada?: number | null;
  segregado: number;  // Quantidade efetivamente segregada pelo auditor
}

export interface UseContagemDataReturn {
  // State
  dadosContagem: DadoContagem[];
  originalDadosContagem: DadoContagem[];
  modifiedItems: Map<number, Set<string>>;
  selectedRow: number | null;
  loading: boolean;
  error: string | null;

  // Actions
  setDadosContagem: React.Dispatch<React.SetStateAction<DadoContagem[]>>;
  setSelectedRow: (row: number | null) => void;
  updateItem: (rowIndex: number, field: keyof DadoContagem, value: string | number) => void;
  resetData: () => void;
  saveOriginalData: () => void;
  hasItemChanges: () => boolean;
  discardItemChanges: () => void;
  setLoading: (value: boolean) => void;
  setError: (error: string | null) => void;
  clearModifiedItems: () => void;

  // Computed
  totalSobras: number;
  totalFaltas: number;
  totalItems: number;
  itemsWithDivergence: number;
}

export const useContagemData = (): UseContagemDataReturn => {
  const [dadosContagem, setDadosContagem] = useState<DadoContagem[]>([]);
  const [originalDadosContagem, setOriginalDadosContagem] = useState<DadoContagem[]>([]);
  const [modifiedItems, setModifiedItems] = useState<Map<number, Set<string>>>(new Map());
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateItem = useCallback((
    rowIndex: number,
    field: keyof DadoContagem,
    value: string | number
  ) => {
    setDadosContagem(prev => {
      const updated = [...prev];
      const item = { ...updated[rowIndex] };

      // Update the field
      (item as any)[field] = value;

      // Epic 42: Recalculate sobras/faltas if sistema, contado, or faturar changed
      // Formula: diff = contado + faturar - sistema (faturar conta como "contabilizado")
      if (field === 'sistema' || field === 'contado' || field === 'faturar') {
        const faturar = item.faturar || 0;
        const diff = item.contado + faturar - item.sistema;
        item.sobras = diff > 0 ? diff : 0;
        item.faltas = diff < 0 ? Math.abs(diff) : 0;
      }

      updated[rowIndex] = item;
      return updated;
    });

    // Track modified items
    setModifiedItems(prev => {
      const newMap = new Map<number, Set<string>>(prev);
      const existingFields = newMap.get(rowIndex);
      const fields: Set<string> = existingFields ? existingFields : new Set<string>();
      fields.add(field);
      newMap.set(rowIndex, fields);
      return newMap;
    });
  }, []);

  const resetData = useCallback(() => {
    setDadosContagem([]);
    setOriginalDadosContagem([]);
    setModifiedItems(new Map());
    setSelectedRow(null);
    setError(null);
  }, []);

  const saveOriginalData = useCallback(() => {
    setOriginalDadosContagem([...dadosContagem]);
    setModifiedItems(new Map());
  }, [dadosContagem]);

  const hasItemChanges = useCallback(() => {
    return modifiedItems.size > 0;
  }, [modifiedItems]);

  const discardItemChanges = useCallback(() => {
    if (originalDadosContagem.length > 0) {
      setDadosContagem([...originalDadosContagem]);
    }
    setModifiedItems(new Map());
  }, [originalDadosContagem]);

  const clearModifiedItems = useCallback(() => {
    setModifiedItems(new Map());
  }, []);

  // Computed values
  const totalSobras = useMemo(() => {
    return dadosContagem.reduce((sum, item) => sum + (item.sobras || 0), 0);
  }, [dadosContagem]);

  const totalFaltas = useMemo(() => {
    return dadosContagem.reduce((sum, item) => sum + (item.faltas || 0), 0);
  }, [dadosContagem]);

  const totalItems = useMemo(() => {
    return dadosContagem.length;
  }, [dadosContagem]);

  const itemsWithDivergence = useMemo(() => {
    return dadosContagem.filter(item => item.sobras > 0 || item.faltas > 0).length;
  }, [dadosContagem]);

  return {
    // State
    dadosContagem,
    originalDadosContagem,
    modifiedItems,
    selectedRow,
    loading,
    error,

    // Actions
    setDadosContagem,
    setSelectedRow,
    updateItem,
    resetData,
    saveOriginalData,
    hasItemChanges,
    discardItemChanges,
    setLoading,
    setError,
    clearModifiedItems,

    // Computed
    totalSobras,
    totalFaltas,
    totalItems,
    itemsWithDivergence,
  };
};

export default useContagemData;
